import { File as DenthubFile } from 'models/file';
import { ActionOrderInput } from 'models/order-input/action-order';
import { LabOrderInput } from 'models/order-input/lab-order';
import {
    OrderDeliveryDetailsInput,
    OrderDraftInput,
    OrderInput,
    OrderParticipantsInput,
    OrderPatientInput,
} from 'models/order-input/restoration-input';
import { OrderInputBase, Participants, PatientFormData } from 'models/order-input/shared';
import { SimpleOrderInput } from 'models/order-input/simple-order';
import { SupplierOrderInput } from 'models/order-input/supplier-order';
import { actionConstructionToRestorationInput, labConstructionToRestorationInput } from './restoration-input-helper';

/**
 * DD-2506 step 3 — each flow's editing state as the one `OrderInput` backend takes. The
 * flows keep their own form models; this is where they meet the write contract.
 */

/**
 * DD-3847: the backend's Name scalar rejects an empty or blank name, so the steps hold the
 * flow until both names carry a character (the form trims; this is the step's own check).
 */
export const hasRequiredPatientNames = (patient: Partial<PatientFormData> | undefined): boolean =>
    Boolean(patient?.firstName?.trim()) && Boolean(patient?.lastName?.trim());

/**
 * DD-3847: a date typed halfway into a picker is an `Invalid Date` — truthy, yet it
 * serialises to `null` on the wire and fails the non-null `Date!` input. Only a real date
 * lets a step continue.
 */
export const isValidDeliveryDate = (date: Date | string | null | undefined): boolean =>
    !!date && !Number.isNaN(new Date(date).getTime());

export const patientInput = (patient: PatientFormData | undefined): OrderPatientInput => ({
    personalIdentityNumber: patient?.personalIdentityNumber ?? '',
    firstName: patient?.firstName ?? '',
    lastName: patient?.lastName ?? '',
    noPersonalIdentityNumber: Boolean(patient?.noPersonalIdentityNumber),
    ...(patient?.patientCategory?.name
        ? {
              patientCategory: {
                  name: patient.patientCategory.name,
                  minage: patient.patientCategory.minage,
                  maxage: patient.patientCategory.maxage,
                  translatedName: patient.patientCategory.translatedName,
              },
          }
        : {}),
});

export const participantsInput = (participants: Participants | undefined): OrderParticipantsInput => ({
    customParticipantEmails: participants?.customParticipantEmails ?? [],
    linkedClinics: (participants?.linkedClinics ?? []).map((clinicLink) => clinicLink.clinic._id as string),
    linkedDentists: (participants?.linkedDentists ?? []).map((dentistLink) => dentistLink.user._id as string),
});

export const filesInput = (files: DenthubFile[] | undefined): { file: string }[] =>
    (files ?? []).map((file) => ({ file: file._id as string }));

/** The checked lab exceptions, by name. */
export const labExceptionsInput = (labExceptions: Record<string, boolean> | undefined): string[] =>
    Object.keys(labExceptions ?? {}).filter((key) => labExceptions?.[key]);

const deliveryDetailsInput = (
    order: OrderInputBase,
    overrides: Partial<OrderDeliveryDetailsInput> = {},
): OrderDeliveryDetailsInput => ({
    date: order.deliveryDetails.date ?? new Date(),
    time: order.deliveryDetails.time,
    pickUpLocation: order.deliveryDetails.pickUpLocation?._id as string | undefined,
    destination: order.deliveryDetails.destination?._id as string | undefined,
    ...(order.deliveryDetails.isPickUpAtClinic !== undefined
        ? { isPickUpAtClinic: order.deliveryDetails.isPickUpAtClinic }
        : {}),
    ...overrides,
});

/** A dentist's simple order: the clinic orders from its lab. */
export const simpleOrderToOrderInput = (order: SimpleOrderInput): OrderInput => ({
    patient: patientInput(order.patient),
    customer: order.customer?._id as string,
    supplier: order.supplier?._id as string,
    dentist: order.dentist?._id as string | undefined,
    participants: participantsInput(order.participants),
    sample: order.sample,
    deliveryDetails: deliveryDetailsInput(order),
    comment: order.comment,
    files: filesInput(order.files),
    restorations: order.restorations,
});

/** A lab's supplier order: the lab orders from another lab, delivered to itself. */
export const supplierOrderToOrderInput = (order: SupplierOrderInput, currentOrgId: string): OrderInput => {
    const isNew = !order._id;
    return {
        // DD-3494: withheld patient details are omitted entirely, never sent blank.
        patient: order.withholdPatient ? undefined : patientInput(order.patient),
        customer: isNew ? currentOrgId : (order.customer?._id as string),
        supplier: order.supplier?._id as string,
        dentist: order.dentist?._id as string | undefined,
        participants: participantsInput(order.participants),
        sample: order.sample,
        deliveryDetails: deliveryDetailsInput(
            order,
            isNew ? { pickUpLocation: currentOrgId, destination: currentOrgId } : {},
        ),
        comment: order.comment,
        files: filesInput(order.files),
        restorations: order.restorations,
    };
};

/** A clinic's action-based order, complete: the flow's last step sends it. */
export const actionOrderToOrderInput = (order: ActionOrderInput): OrderInput => ({
    patient: patientInput(order.patient),
    customer: order.customer?._id as string,
    supplier: order.supplier?._id as string,
    dentist: order.dentist?._id as string | undefined,
    participants: participantsInput(order.participants),
    sample: order.sample,
    deliveryDetails: deliveryDetailsInput(order),
    labExceptions: labExceptionsInput(order.labExceptions),
    comment: order.comment,
    files: filesInput(order.files),
    vendorReference: order.vendorReference,
    restorations: order.constructions.map(actionConstructionToRestorationInput),
});

/** What the action flow's patient step saves on the draft. */
export const actionOrderPatientDraft = (order: ActionOrderInput): OrderDraftInput => ({
    patient: patientInput(order.patient),
    customer: order.customer?._id as string,
    dentist: order.dentist?._id as string | undefined,
    participants: participantsInput(order.participants),
    vendorReference: order.vendorReference,
});

/** What the action flow's constructions step saves on the draft. */
export const actionOrderConstructionsDraft = (order: ActionOrderInput): OrderDraftInput => ({
    comment: order.comment,
    files: filesInput(order.files),
    restorations: order.constructions.map(actionConstructionToRestorationInput),
});

/**
 * What the action flow's lab and delivery step saves on the draft. The restorations go
 * along again: with the lab known they are priced from the connection.
 */
export const actionOrderDeliveryDraft = (order: ActionOrderInput): OrderDraftInput => ({
    supplier: order.supplier?._id as string,
    labExceptions: labExceptionsInput(order.labExceptions),
    deliveryDetails: deliveryDetailsInput(order, {
        isPickUpAtClinic: order.deliveryDetails.isPickUpAtClinic || false,
    }),
    sample: order.sample,
    restorations: order.constructions.map(actionConstructionToRestorationInput),
});

const hasRows = (order: LabOrderInput) =>
    order.constructions.some((construction) => construction.actions.length > 0 || construction.materials.length > 0);

/** A lab-created order (also from a scanner case or an order form). */
export const labOrderToOrderInput = (order: LabOrderInput, currency: string): OrderInput => ({
    patient: patientInput(order.patient),
    // The lab files the order on the clinic it delivers to; a delivery clinic under a
    // parent is resolved to the parent's catalogue by the server.
    customer: (order.deliveryDetails.destination?._id ?? order.customer?._id) as string,
    supplier: order.supplier?._id as string,
    dentist: order.dentist?._id as string | undefined,
    sample: order.sample,
    deliveryDetails: {
        date: order.deliveryDetails.date ?? new Date(),
        time: order.deliveryDetails.time,
        internalDeliveryDate: order.internalDeliveryDate || null,
        externalDeliveryDate: order.externalDeliveryDate || null,
    },
    comment: order.comment,
    files: filesInput(order.files),
    restorations: order.constructions.map(labConstructionToRestorationInput),
    currency: hasRows(order) ? currency : 'XXX',
    manualSelectedReceivedDate: order.sampleReceivedDate,
    orderFormId: order.orderForm?._id || undefined,
    orderStep: order.workflowStep,
    panNumber: order.panNumber || undefined,
    panNumberDescription: order.panNumberDescription || undefined,
    internalOrderId: order.internalOrderId || undefined,
    scannerCaseId: order.scannerCase?._id || undefined,
    productionPlan: order.productionPlanningSteps?.length
        ? order.productionPlanningSteps.map((step, index) => ({
              stepId: step.stepId,
              stepName: step.stepName,
              startDate: step.startDate || undefined,
              deadline: step.deadline || undefined,
              estimatedProductionHours: step.estimatedProductionHours || 0,
              estimatedMachineHours: step.estimatedMachineHours || 0,
              allowOvernight: step.allowOvernight ?? false,
              startNextDay: step.startNextDay ?? false,
              stepOrder: index + 1,
              workUnits: step.workUnits || 0,
              departmentId: step.departmentId || undefined,
              machineId: step.machineId || undefined,
          }))
        : undefined,
});
